import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical } from 'lucide-react';
import { Portal } from './Portal';

export interface MenuOption {
  key: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
}

interface MoreOptionsMenuProps {
  options: MenuOption[];
  className?: string;
}

export const MoreOptionsMenu: React.FC<MoreOptionsMenuProps> = ({
  options,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Calculate menu position when opening
  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 200; // Approximate menu width

      // Position below the button, aligned to the right
      let left = rect.right - menuWidth;
      const top = rect.bottom + 4;

      // Ensure menu doesn't go off-screen to the left
      if (left < 8) {
        left = 8;
      }

      setMenuPosition({ top, left });
    }
    setIsOpen(!isOpen);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleOptionClick = (option: MenuOption) => {
    option.onClick();
    setIsOpen(false);
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={`p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors ${className}`}
        aria-label="More options"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <MoreVertical className="w-5 h-5" />
      </button>

      {isOpen && (
        <Portal>
          <div
            ref={menuRef}
            className="fixed bg-white border border-gray-200 shadow-lg py-1 z-50 min-w-[180px]"
            style={{
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
            }}
            role="menu"
          >
            {options.map((option) => (
              <button
                key={option.key}
                onClick={() => handleOptionClick(option)}
                className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 text-left"
                role="menuitem"
              >
                {option.icon && <span className="flex-shrink-0">{option.icon}</span>}
                <span>{option.label}</span>
              </button>
            ))}
          </div>
        </Portal>
      )}
    </>
  );
};
